import { cn } from '@/lib/cn';

/**
 * Um número de painel, escrito como se lê numa trena: o valor grande e a unidade menor,
 * colada nele. `1760h` vira **1.760** h; `59%` vira **59** %.
 *
 * O que não começa por dígito — `R$ 184.320,00`, `—` — sai como veio. O componente não
 * formata valor de negócio; só separa a unidade do número que já chegou formatado.
 */
const FIGURE_SIZE = {
  md: 'text-2xl',
  lg: 'text-3xl md:text-[2rem]',
  xl: 'text-4xl',
} as const;

const FIGURE_TONE = {
  default: 'text-(--color-content)',
  ok: 'text-(--color-state-ok)',
  warn: 'text-(--color-state-warn)',
  danger: 'text-(--color-state-danger)',
} as const;

export type FigureTone = keyof typeof FIGURE_TONE;

export interface FigureProps {
  readonly value: string;
  readonly size?: keyof typeof FIGURE_SIZE;
  readonly tone?: FigureTone;
  readonly className?: string;
}

const NUMBER_AND_UNIT = /^([+\-−]?)(\d+)(.*)$/u;

function groupThousands(digits: string): string {
  return Number(digits).toLocaleString('pt-BR');
}

export function Figure({ value, size = 'lg', tone = 'default', className }: FigureProps) {
  const parts = NUMBER_AND_UNIT.exec(value);
  const base = cn(
    'tabular font-semibold leading-none tracking-tight',
    FIGURE_SIZE[size],
    FIGURE_TONE[tone],
    className,
  );

  if (parts === null) return <span className={base}>{value}</span>;

  const [, sign = '', digits = '', unit = ''] = parts;
  return (
    <span className={base}>
      {sign}
      {groupThousands(digits)}
      {unit !== '' && (
        <span className="ml-px text-[0.55em] font-medium text-(--color-content-muted)">{unit}</span>
      )}
    </span>
  );
}
